import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress,
  Grid,
  Paper,
  Card,
  CardMedia,
  CardContent,
  Alert,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EventIcon from '@mui/icons-material/Event';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { format, isValid } from 'date-fns';
import { db } from '../../firebase/config';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  updateDoc, 
  arrayUnion,
  increment, 
  Timestamp
} from 'firebase/firestore';
import trackingService from '../../services/trackingService';

// Ticket type interface for event registration
interface TicketType {
  id: string;
  name: string;
  description?: string;
  price: number;
  capacity: number;
  ticketsSold: number;
  isVisible: boolean;
}

// Event data interface for public events
interface EventData {
   id: string;
   title: string;
   description: string;
   eventType: string;
   location: string | any;
   startDate: any;
   endDate: any;
   isPaid: boolean;
   price: number;
   maxCapacity: number;
   ticketsSold: number;
   coverPhotoUrl?: string;
   organizationId: string;
   publicLinkId: string;
   isPublic: boolean;
   attendees: any[];
   ticketTypes?: TicketType[];
}

interface OrganizationData {
  id: string;
  name: string;
  logo?: string;
}

// Registration form data for simplified embed experience
interface RegistrationFormData {
  name: string;
  email: string;
  phone: string;
  selectedTicketType: string;
  ticketQuantity: number;
}

// Main component optimized for iframe embedding
const EventEmbedView: React.FC = () => {
  const { publicLinkId } = useParams<{ publicLinkId: string }>();
  const location = useLocation();
  const [event, setEvent] = useState<EventData | null>(null);
  const [organization, setOrganization] = useState<OrganizationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Registration form with simplified fields for embedding
  const [formData, setFormData] = useState<RegistrationFormData>({
    name: '',
    email: '',
    phone: '',
    selectedTicketType: '',
    ticketQuantity: 1
  });
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Track embed view
  useEffect(() => {
    const trackEmbedView = async () => {
      if (event && publicLinkId) {
        try {
          // Get tracking info from URL params if available
          const params = new URLSearchParams(location.search);
          const trackingId = params.get('share_id');
          const referrer = document.referrer || 'direct';
          
          if (trackingId) {
            // Record a click/view using existing tracking ID
            await trackingService.recordClick(
              trackingId,
              referrer,
              navigator.userAgent,
              '', // We don't hash IPs for privacy
              undefined,
              { viewType: 'embed' }
            );
          } else {
            // Generate a new tracking ID for this embed if none exists
            const { trackingId, shareUrl } = trackingService.generateTrackableUrl(
              window.location.href,
              event.id,
              'event',
              'embed' as any // Adding embed as a platform type
            );
            
            // Record this as a new share via embed
            await trackingService.recordShare(
              event.id,
              'event',
              'embed' as any, // Using embed as a platform type
              shareUrl,
              trackingId,
              event.organizationId,
              event.id,
              undefined,
              undefined,
              { embedSource: referrer }
            );
          }
        } catch (err) {
          console.error('Error tracking embed view:', err);
          // Non-blocking - continue showing event even if tracking fails
        }
      }
    };
    
    if (event) {
      trackEmbedView();
    }
  }, [event, location.search, publicLinkId]);

  // Fetch event details
  useEffect(() => {
    if (publicLinkId) {
      fetchEventDetails();
    } else {
      setError('Invalid event link');
      setLoading(false);
    }
  }, [publicLinkId]);
  
  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      
      // First try to find the event using only the publicLinkId
      // This is more permissive and will work regardless of which public flag is used
      let eventsQuery = query(
        collection(db, 'events'),
        where('publicLinkId', '==', publicLinkId)
      );
      
      let querySnapshot = await getDocs(eventsQuery);
      
      if (querySnapshot.empty) {
        setError('Event not found or no longer available');
        setLoading(false);
        return;
      }
      
      // Get the first matching event
      const eventDoc = querySnapshot.docs[0];
      const eventData = eventDoc.data();
      
      // Ensure all necessary fields have valid values
      const eventWithDefaults = {
        ...eventData,
        id: eventDoc.id,
        ticketsSold: eventData.ticketsSold || 0,
        attendees: eventData.attendees || [],
        ticketTypes: eventData.ticketTypes || []
      } as EventData;
      
      setEvent(eventWithDefaults);
      
      // Get organization details
      if (eventData.organizationId) {
        const orgDoc = await doc(db, 'organizations', eventData.organizationId);
        const orgSnapshot = await getDocs(query(collection(db, 'organizations'), where('__name__', '==', eventData.organizationId)));
        
        if (!orgSnapshot.empty) {
          const orgData = orgSnapshot.docs[0].data();
          setOrganization({
            id: orgSnapshot.docs[0].id,
            name: orgData.name || 'Organization',
            logo: orgData.logo
          });
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Error loading event details');
      setLoading(false);
    }
  };
  
  // Utility functions for formatting dates and currency
  const formatDate = (date: any) => {
    if (!date) return 'TBD';
    
    try {
      // Handle Firestore Timestamp
      if (date.toDate) {
        return format(date.toDate(), 'EEEE, MMMM d, yyyy');
      }
      
      // Handle string date
      if (typeof date === 'string') {
        return format(new Date(date), 'EEEE, MMMM d, yyyy');
      }
      
      // Handle Date object
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Invalid date';
    }
  };
  
  const formatTime = (date: any) => {
    if (!date) return 'TBD';
    
    try {
      // Handle Firestore Timestamp
      if (date.toDate) {
        return format(date.toDate(), 'h:mm a');
      }
      
      // Handle string date
      if (typeof date === 'string') {
        return format(new Date(date), 'h:mm a');
      }
      
      // Handle Date object
      return format(date, 'h:mm a');
    } catch (err) {
      console.error('Error formatting time:', err);
      return 'Invalid time';
    }
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Form handling
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name) {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'ticketQuantity' ? Number(value) : value
      }));
    }
  };
  
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!formData.selectedTicketType) {
      errors.selectedTicketType = 'Please select a ticket type';
    }
    
    if (formData.ticketQuantity < 1) {
      errors.ticketQuantity = 'Quantity must be at least 1';
    }
    
    const selectedTicket = event?.ticketTypes?.find(t => t.id === formData.selectedTicketType);
    if (selectedTicket && (selectedTicket.capacity - selectedTicket.ticketsSold) < formData.ticketQuantity) {
      errors.ticketQuantity = 'Not enough tickets available';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !event) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const selectedTicket = event.ticketTypes?.find(t => t.id === formData.selectedTicketType);
      if (!selectedTicket) {
        throw new Error('Selected ticket type not found');
      }
      
      // Process registration
      const ticketPrice = selectedTicket.price;
      const totalAmount = ticketPrice * formData.ticketQuantity;
      
      if (ticketPrice > 0) {
        // For paid events, we'd normally show payment UI
        // In embedded view, just simulate payment processing
        setProcessingPayment(true);
        
        // Simulate payment processing delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setProcessingPayment(false);
      }
      
      // Update the event with the new attendee
      const eventRef = doc(db, 'events', event.id);
      
      // Add the attendee
      const attendee = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        ticketType: formData.selectedTicketType,
        ticketTypeName: selectedTicket.name,
        ticketQuantity: formData.ticketQuantity,
        amountPaid: totalAmount,
        purchaseDate: Timestamp.now(),
        source: 'embed', // Track that this came from an embedded view
        referrer: document.referrer || 'direct'
      };
      
      await updateDoc(eventRef, {
        attendees: arrayUnion(attendee),
        ticketsSold: increment(formData.ticketQuantity),
        [`ticketTypes.${event.ticketTypes?.findIndex(t => t.id === formData.selectedTicketType)}.ticketsSold`]: 
          increment(formData.ticketQuantity)
      });
      
      // Record this conversion for tracking
      try {
        const params = new URLSearchParams(location.search);
        const trackingId = params.get('share_id');
        
        if (trackingId) {
          await trackingService.recordClick(
            trackingId,
            document.referrer || 'direct',
            navigator.userAgent,
            '',
            undefined,
            { 
              conversion: true, 
              conversionType: 'purchase',
              conversionValue: totalAmount,
              ticketType: selectedTicket.name,
              ticketQuantity: formData.ticketQuantity
            }
          );
        }
      } catch (trackErr) {
        console.error('Error recording conversion:', trackErr);
        // Non-blocking - continue with registration
      }
      
      setRegistrationSuccess(true);
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        selectedTicketType: '',
        ticketQuantity: 1
      });
      
    } catch (error) {
      console.error('Error registering for event:', error);
      setError('Failed to register for the event. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Component to show while loading
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Component to show if there's an error
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  // Component to show if event is not found
  if (!event) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Event not found or no longer available.</Alert>
      </Box>
    );
  }
  
  // Success message after registration
  if (registrationSuccess) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Thank you for registering for {event.title}!
        </Alert>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We've sent a confirmation email to your provided email address.
          You'll receive more information about the event soon.
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => setRegistrationSuccess(false)}
        >
          Register Another Attendee
        </Button>
      </Box>
    );
  }
  
  // Determine if we have tickets available
  const hasAvailableTickets = event.ticketTypes?.some(ticket => 
    ticket.isVisible && (ticket.capacity - ticket.ticketsSold) > 0
  ) ?? false;
  
  // Main component render
  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
      {/* Header with event image */}
      <Box sx={{ position: 'relative' }}>
        {event.coverPhotoUrl ? (
          <Box
            component="img"
            src={event.coverPhotoUrl}
            alt={event.title}
            sx={{
              width: '100%',
              height: 200,
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box
            sx={{
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.light'
            }}
          >
            <EventIcon sx={{ fontSize: 60, color: 'white', opacity: 0.7 }} />
          </Box>
        )}
        
        {/* Organization logo overlay */}
        {organization?.logo && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '50%',
              p: 0.5,
              height: 50,
              width: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              component="img"
              src={organization.logo}
              alt={organization.name}
              sx={{
                maxHeight: 40,
                maxWidth: 40,
                objectFit: 'contain',
                borderRadius: '50%'
              }}
            />
          </Box>
        )}
      </Box>
      
      {/* Event Info */}
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          {event.title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body1">
            {formatDate(event.startDate)} • {formatTime(event.startDate)}
            {event.endDate && event.endDate !== event.startDate && 
              ` - ${formatTime(event.endDate)}`}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body1">
            {typeof event.location === 'string' ? event.location : 
              (event.location as any)?.name || 'Location TBD'}
          </Typography>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Registration Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Register for this Event
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                id="name"
                name="name"
                label="Full Name"
                value={formData.name}
                onChange={handleFormChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                id="email"
                name="email"
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                id="phone"
                name="phone"
                label="Phone Number"
                value={formData.phone}
                onChange={handleFormChange}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!formErrors.selectedTicketType}>
                <InputLabel id="ticket-type-label">Ticket Type</InputLabel>
                <Select
                  labelId="ticket-type-label"
                  id="selectedTicketType"
                  name="selectedTicketType"
                  value={formData.selectedTicketType}
                  label="Ticket Type"
                  onChange={handleSelectChange}
                >
                  {event.ticketTypes
                    ?.filter(ticket => ticket.isVisible && (ticket.capacity - ticket.ticketsSold) > 0)
                    .map(ticket => (
                      <MenuItem 
                        key={ticket.id} 
                        value={ticket.id}
                      >
                        {ticket.name} - {formatCurrency(ticket.price)} 
                        {ticket.description && ` (${ticket.description})`}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.selectedTicketType && (
                  <FormHelperText>{formErrors.selectedTicketType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!formErrors.ticketQuantity}>
                <InputLabel id="ticket-quantity-label">Quantity</InputLabel>
                <Select
                  labelId="ticket-quantity-label"
                  id="ticketQuantity"
                  name="ticketQuantity"
                  value={formData.ticketQuantity.toString()}
                  label="Quantity"
                  onChange={handleSelectChange}
                >
                  {[...Array(10)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                  ))}
                </Select>
                {formErrors.ticketQuantity && (
                  <FormHelperText>{formErrors.ticketQuantity}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={submitting || processingPayment || !hasAvailableTickets}
              sx={{ px: 4, py: 1 }}
            >
              {processingPayment ? 'Processing Payment...' : 
               submitting ? 'Registering...' : 
               !hasAvailableTickets ? 'Sold Out' : 
               'Complete Registration'}
              {(submitting || processingPayment) && 
                <CircularProgress size={24} sx={{ ml: 1 }} color="inherit" />}
            </Button>
          </Box>
        </Box>
        
        {/* Powered by footer */}
        <Box sx={{ mt: 3, textAlign: 'center', opacity: 0.7 }}>
          <Typography variant="caption">
            Powered by {organization?.name || 'wetrippen'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default EventEmbedView;
